import type { AnalyticCategoryActions } from 'models/analytics';
import { getFrontLogger } from 'utils/loggers/FrontLogger';

declare global {
  interface Window {
    _paq: any[];
  }
}

interface ILogAnalyticConfig {
  companyName: string;
  isUserInternal: boolean;
  login: string;
  matomoBaseUrl: string;
  matomoSiteId?: number;
}

export type Category = keyof AnalyticCategoryActions;

export interface ILogAnalytics {
  initialize(config: ILogAnalyticConfig): void;

  changeUserConsent(consentUserTracking: boolean): void;

  /**
   * Tracks the change of page
   * @param hash The content of url after hash (including hash)
   */
  changePage(hash: string): void;
  /**
   * Tracks an event
   * @param category The event category
   * @param action The event action
   * @param name The optional event action name
   */
  trackEvent<C extends Category>(category: C, action: AnalyticCategoryActions[C], name?: string): void;
  scanForForms(): void;
  trackSubmitForm(formElement: HTMLElement): void;
}

function appendPiwikScript(piwikSourceUrl: string): void {
  const firstDocumentScriptNode = document.getElementsByTagName('script')[0];
  const piwikScript = document.createElement('script');

  piwikScript.type = 'text/javascript';
  piwikScript.async = true;
  piwikScript.defer = true;
  piwikScript.src = piwikSourceUrl;

  if (firstDocumentScriptNode === null || firstDocumentScriptNode.parentNode === null) {
    throw new Error('Can not inject piwik source code into document');
  }

  firstDocumentScriptNode.parentNode.insertBefore(piwikScript, firstDocumentScriptNode);
}

let isInitialized: boolean = false;
let hadGivenConsentPreviously: boolean = false;

export const logAnalytics = {
  changeUserConsent(userConsent: boolean) {
    if (!isInitialized) {
      getFrontLogger().error('Matomo/Piwik user tracking cookie policy ignored as analytics is not yet started');
      return;
    }
    getFrontLogger().info(
      `Matomo/Piwik user tracking ${userConsent ? 'ENABLED' : 'DISABLED'} according to cookie policy`,
    );
    if (userConsent) {
      window._paq.push(['rememberCookieConsentGiven', 6 * 30 * 24]); //6 months * 30 days * 24 hours.
      window._paq.push(['setCustomDimension', 10, true]);
      hadGivenConsentPreviously = true;
    } else {
      if (hadGivenConsentPreviously) {
        window._paq.push(['forgetCookieConsentGiven']);
        hadGivenConsentPreviously = false;
      }
      window._paq.push(['setCustomDimension', 10, false]);
    }
  },
  initialize(config: ILogAnalyticConfig): boolean {
    if (isInitialized || !config.matomoBaseUrl || config.matomoBaseUrl.startsWith('{{') || !config.matomoSiteId) {
      return false;
    }
    getFrontLogger().info(`Matomo/Piwik initializing on '${config.matomoBaseUrl}' with siteId ${config.matomoSiteId}`);

    const currentDomain = window.location.hostname;

    window._paq = [];
    window._paq.push(['setDomains', [currentDomain]]);
    window._paq.push(['requireCookieConsent']); // must be before 'trackPageView' (GDPR).
    window._paq.push(['trackPageView']);
    window._paq.push(['enableLinkTracking']);
    window._paq.push(['setTrackerUrl', config.matomoBaseUrl + '/piwik.php']);
    window._paq.push(['setSiteId', config.matomoSiteId]);

    window._paq.push(['setCustomVariable', 3, 'user-type', config.isUserInternal ? 'internal' : 'external', 'visit']);
    window._paq.push(['setCustomVariable', 2, 'user-company', config.companyName, 'visit']);
    window._paq.push(['setUserId', config.login]);

    const isHomlogation = config.matomoBaseUrl.includes('-uat');

    if (isHomlogation) {
      window._paq.push(['FormAnalytics::enableDebugMode']);
    }

    appendPiwikScript(config.matomoBaseUrl + '/piwik.js');

    isInitialized = true;

    return true;
  },

  changePage(hash: string) {
    if (!isInitialized) {
      return;
    }

    const currentUrl = '/' + hash;
    window._paq.push(['setCustomUrl', currentUrl]);
    window._paq.push(['setDocumentTitle', hash]);

    window._paq.push(['deleteCustomVariables', 'page']);
    window._paq.push(['trackPageView']);

    // make Matomo aware of newly added content
    window._paq.push(['enableLinkTracking']);
  },

  trackEvent(category, action, name) {
    if (!isInitialized) {
      return;
    }

    const data = ['trackEvent', category, action];
    if (name) {
      data.push(name);
    }
    window._paq.push(data);
  },

  scanForForms() {
    if (!isInitialized) {
      return;
    }
    window._paq.push(['FormAnalytics::scanForForms']);
  },

  trackSubmitForm(formElement: HTMLElement) {
    if (!isInitialized) {
      return;
    }
    window._paq.push(['FormAnalytics::trackFormSubmit', formElement]);
    window._paq.push(['FormAnalytics::trackFormConversion', formElement]);
  },
} as ILogAnalytics;
